import {Injectable} from '@angular/core';
import {ActivatedRoute, convertToParamMap, ParamMap, Router} from '@angular/router';
import {SnackbarService} from '../snackbar/snackbar.service';
import {DateRangeSelectorComponent} from '../../components/layout/date-range-selector/date-range-selector.component';

export interface StatsPeriodParam {
  preset: string;
  from: string;
  to: string;
}

export interface StatsPeriodParamNames {
  period: string;
  start: string;
  end: string;
}

const PERIOD_PRESETS = [
  'today',
  'yesterday',
  'this-week',
  'last-week',
  'this-month',
  'last-month',
  'this-year',
  'last-year',
];

const DEFAULT_NAMES: StatsPeriodParamNames = {period: 'period', start: 'startDate', end: 'endDate'};

@Injectable({
  providedIn: 'root'
})
export class StatsFilterUrlService {
  constructor(
    private router: Router,
    private snackbar: SnackbarService,
  ) {
  }

  getInitialParams(route: ActivatedRoute): ParamMap {
    const params = route.snapshot.queryParamMap;

    if (params.keys.length) {
      return params;
    }

    const saved = this.getSavedParams(route);

    return saved ? convertToParamMap(saved) : params;
  }

  getPeriod(params: ParamMap, defaultPreset: string, names: StatsPeriodParamNames = DEFAULT_NAMES): StatsPeriodParam {
    const from = this.normalizeDate(params.get(names.start));
    const to = this.normalizeDate(params.get(names.end)) ?? from;

    if (from && to) {
      return {preset: 'custom', from, to};
    }

    const preset = params.get(names.period);

    return {preset: preset && PERIOD_PRESETS.includes(preset) ? preset : defaultPreset, from: '', to: ''};
  }

  getPeriodParams(selector: DateRangeSelectorComponent, names: StatsPeriodParamNames = DEFAULT_NAMES): any {
    if (selector.dateSelector !== 'custom') {
      return {[names.period]: selector.dateSelector};
    }

    return {[names.start]: selector.dateFromSelector, [names.end]: selector.dateToSelector};
  }

  resetPeriod(selector: DateRangeSelectorComponent, preset: string): void {
    selector.dateSelector = preset;
    selector.computeDate();
  }

  update(route: ActivatedRoute, params: any): void {
    this.router.navigate([], {
      relativeTo: route,
      queryParams: this.toQueryParams(params),
      replaceUrl: true,
    }).then();
  }

  save(route: ActivatedRoute, params: any): void {
    try {
      localStorage.setItem(this.getStorageKey(route), JSON.stringify(this.toQueryParams(params)));
      this.snackbar.success('Filters have been saved');
    } catch {
      this.snackbar.error('Filters could not be saved in this browser');
    }
  }

  clear(route: ActivatedRoute): void {
    try {
      localStorage.removeItem(this.getStorageKey(route));
    } catch {
    }
  }

  private toQueryParams(params: any): any {
    const queryParams: any = {};

    for (const [name, value] of Object.entries(params ?? {})) {
      if (value === null || value === undefined || value === '') {
        continue;
      }

      if (Array.isArray(value)) {
        if (value.length) {
          queryParams[name] = value.join(',');
        }
        continue;
      }

      queryParams[name] = value;
    }

    return queryParams;
  }

  private getSavedParams(route: ActivatedRoute): any | null {
    try {
      const params = JSON.parse(localStorage.getItem(this.getStorageKey(route)) ?? 'null');

      return params && typeof params === 'object' && Object.keys(params).length ? params : null;
    } catch {
      return null;
    }
  }

  private getStorageKey(route: ActivatedRoute): string {
    const segments = route.snapshot.pathFromRoot.flatMap(snapshot => snapshot.url.map(segment => segment.path));

    return 'list-filters:/' + segments.join('/');
  }

  private normalizeDate(value: string | null): string | null {
    return value && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : null;
  }
}
